import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import {
  CreateStructureListEntryDto,
  IPaginatedList,
  StructureListEntryDto,
  UpdateStructureListEntryDto
} from '@coin/importer/dto/util';
import { environment } from '@coin/shared/core/util-environments';
import { firstValueFrom, Observable, ReplaySubject, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { PaginatedResult } from '@coin/shared/core/util-models';
import { ToastService } from '../toast/toast.service';
import { AcceptedParams } from '../../components/simple-table/simple-table.component';

@Injectable({
  providedIn: 'root'
})
export class StructureListService {
  private http = inject(HttpClient);
  private toast = inject(ToastService);
  private router = inject(Router);
  private route = inject(ActivatedRoute);

  private newEntrySource$ = new ReplaySubject<StructureListEntryDto>(null);
  newEntry$ = this.newEntrySource$.asObservable();

  private api = [environment.api.importerService, 'organisation-management/structure-list'].join('/');

  getEntries(params: Params): Observable<IPaginatedList<StructureListEntryDto>> {
    return this.http
      .get<IPaginatedList<StructureListEntryDto>>(this.api, {
        params
      })
      .pipe(catchError(this.handleError.bind(this)));
  }

  async createEntry(entry: CreateStructureListEntryDto) {
    return firstValueFrom(this.http.put(this.api, entry).pipe(catchError(this.handleError.bind(this))));
  }

  async updateEntry(entry: UpdateStructureListEntryDto) {
    return firstValueFrom(this.http.patch(this.api, entry).pipe(catchError(this.handleError.bind(this))));
  }

  async deleteEntry(gid: string) {
    return firstValueFrom(this.http.delete(`${this.api}/${gid}`).pipe(catchError(this.handleError.bind(this))));
  }

  queryPagedAndFilteredData(params: AcceptedParams) {
    const sortedParams = this.sortParams(params);
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: sortedParams
    });
    return this.getEntries(sortedParams).pipe(map(this.fitToPaginatedResult));
  }

  private fitToPaginatedResult(obj: IPaginatedList<StructureListEntryDto>): PaginatedResult<StructureListEntryDto> {
    const result: PaginatedResult<StructureListEntryDto> = {
      content: obj.items,
      currentPage: obj.meta.currentPage,
      nextPage: obj.meta.currentPage + 1,
      pageCount: obj.meta.totalPages,
      pageSize: obj.meta.itemsPerPage,
      total: obj.meta.totalItems
    };
    return result;
  }

  setTemporaryLog(element: StructureListEntryDto): void {
    this.newEntrySource$.next(element);
  }

  // ---

  // Following duplicate to LogsService,
  private sortParams(params: AcceptedParams): Params {
    const filteredParams: Params = {};
    let param: keyof typeof params;
    for (param in params) {
      const value = decodeURI(params[param] as string);
      switch (param) {
        case 'page': {
          const page = parseInt(value);
          if (page > 1) {
            // page=1 is default and should not show in url
            filteredParams[param] = page;
          }
          break;
        }
        case 'limit': {
          const limit = parseInt(value);
          if (limit > 0 && limit !== environment.importerTableLimit) {
            // limit=25 is default and should not show in url
            filteredParams[param] = limit;
          }
          break;
        }
        case 'search':
          if (value) {
            this.toast.info('Search-Parameter not yet implemented');
          }
          break;
        default:
          if (value.length > 0) {
            filteredParams[param] = value.includes('&') ? value.split('&') : value;
          }
          break;
      }
    }
    return filteredParams;
  }

  private handleError(error: HttpErrorResponse) {
    if (error.status === 0) {
      this.toast.error('The Request could not be sent - please try again later');
    } else if (error.status === 404) {
      this.toast.errorWithRedirect(`The requested page could not be found.`, '404');
    } else {
      this.toast.error(`Error: "${error.error?.message || 'unknown'}"`);
    }
    return throwError(error);
  }
}
